/**
 * Stage 22 · Autonomous Operations — Automation Centre.
 * Never black-box. Every action is explainable, versioned and reversible.
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bot, Radar, ScrollText, Activity, ShieldAlert, DollarSign, Pause, Play, AlertOctagon, GitBranch, Sparkles } from "lucide-react";

const RISK_STYLE = {
  0: "bg-slate-100 text-slate-600",
  1: "bg-sky-100 text-sky-800",
  2: "bg-emerald-100 text-emerald-800",
  3: "bg-amber-100 text-amber-800",
  4: "bg-orange-100 text-orange-800",
  5: "bg-rose-100 text-rose-800",
};
const MODE_STYLE = {
  draft: "bg-slate-100 text-slate-500 border-slate-200",
  simulation: "bg-slate-100 text-slate-700 border-slate-200",
  shadow: "bg-sky-100 text-sky-800 border-sky-200",
  pilot: "bg-amber-100 text-amber-800 border-amber-200",
  production: "bg-emerald-100 text-emerald-800 border-emerald-200",
  paused: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function Automation() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="max-w-6xl space-y-5" data-testid="automation-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Automation · Autonomous Operations</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Automation Centre</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-3xl">
          Rules, agents and event-driven workflows. Every automation is explainable, versioned and reversible.
          Levels 4-5 require maker-checker. Level 5 is never autonomous. Simulation → Shadow → Pilot → Production.
        </p>
      </div>
      <nav className="flex flex-wrap gap-1 border-b border-slate-200">
        {[
          { key: "overview",    label: "Overview",       icon: Activity },
          { key: "automations", label: "Automations",     icon: GitBranch },
          { key: "agents",      label: "AI Agents",       icon: Bot },
          { key: "runs",        label: "Runs",            icon: Radar },
          { key: "approvals",   label: "Approvals",       icon: Sparkles },
          { key: "exceptions",  label: "Exceptions",      icon: AlertOctagon },
          { key: "impact",      label: "Savings / Impact",icon: DollarSign },
          { key: "admin",       label: "Admin",           icon: ShieldAlert },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`auto-tab-${key}`}
            className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px " +
              (tab === key ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>
      {tab === "overview"    && <Overview />}
      {tab === "automations" && <Automations />}
      {tab === "agents"      && <Agents />}
      {tab === "runs"        && <Runs />}
      {tab === "approvals"   && <Approvals />}
      {tab === "exceptions"  && <Exceptions />}
      {tab === "impact"      && <Impact />}
      {tab === "admin"       && <AdminTab />}
    </div>
  );
}

function Chip({ color, children, testid }) {
  return <span className={"inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + color} data-testid={testid}>{children}</span>;
}
function Tile({ label, value, tone, testid }) {
  const cls = tone === "warn" ? "text-amber-700" : tone === "critical" ? "text-rose-700" : tone === "ok" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"mt-1 text-2xl font-heading font-semibold tabular-nums " + cls}>{value ?? "—"}</div>
    </div>
  );
}

/* ---- Overview ---- */
function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/automation/overview").then(({ data }) => setD(data)).catch(() => setD(null)); }, []);
  if (!d) return <div className="text-sm text-slate-500">Loading…</div>;
  const t = d.tiles || {};
  return (
    <div className="space-y-4" data-testid="auto-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Total rules"          value={t.total_rules} testid="auto-tile-total" />
        <Tile label="Active"                value={t.active_rules} tone="ok" />
        <Tile label="Production"            value={t.production_rules} tone="ok" />
        <Tile label="Shadow / testing"      value={t.shadow_rules} />
        <Tile label="Pending approvals"     value={t.pending_approvals} tone="warn" testid="auto-tile-approvals" />
        <Tile label="Open exceptions"       value={t.open_exceptions} tone="warn" />
        <Tile label="Runs (24h)"            value={t.runs_last_24h} />
        <Tile label="Failures (24h)"        value={t.failures_last_24h} tone="critical" />
      </div>
      {d.kill_switch?.paused_global && (
        <div className="bg-rose-50 border border-rose-200 text-rose-900 rounded-md p-3 text-sm font-medium" data-testid="auto-kill-banner">
          Global kill switch is engaged. No automation will run until unpaused in the Admin tab.
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="p-3 border-b border-slate-200 text-sm font-medium">Agent status</div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">Agent</th><th className="text-left py-2 px-3">Risk ceiling</th><th className="text-left py-2 px-3">Status</th></tr>
          </thead>
          <tbody>
            {(d.agents || []).map((a) => (
              <tr key={a.code} className="border-t border-slate-100">
                <td className="py-2 px-3 text-slate-900">{a.name}</td>
                <td className="py-2 px-3"><Chip color={RISK_STYLE[a.risk_ceiling]}>L{a.risk_ceiling}</Chip></td>
                <td className="py-2 px-3"><Chip color={a.paused ? "bg-rose-100 text-rose-800 border border-rose-200" : "bg-emerald-100 text-emerald-800 border border-emerald-200"}>{a.paused ? "paused" : "available"}</Chip></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---- Automations ---- */
function Automations() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => api.get("/automation/rules").then(({ data }) => setItems(data.items || [])), []);
  useEffect(() => { load(); }, [load]);
  const transition = async (id, mode) => {
    try { await api.post(`/automation/rules/${id}/mode?mode=${mode}`); toast.success(`→ ${mode}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const simulate = async (id) => {
    try {
      const { data } = await api.post(`/automation/rules/${id}/simulate?days_back=30`);
      toast.success(`Simulation: ${data.would_match} matches → ${JSON.stringify(data.would_perform)}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="auto-rules">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="auto-rule-new">New automation</button>
      </div>
      {showForm && <RuleForm onCancel={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">Name</th><th className="text-left py-2 px-3">Event</th><th className="text-left py-2 px-3">Risk</th><th className="text-left py-2 px-3">Mode</th><th className="text-left py-2 px-3">v</th><th className="text-right py-2 px-3">Actions</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-slate-500">No automations yet.</td></tr>
            ) : items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`auto-rule-${r.id}`}>
                <td className="py-2 px-3 text-slate-900">{r.name}</td>
                <td className="py-2 px-3 font-mono text-xs text-slate-600">{r.when_event}</td>
                <td className="py-2 px-3"><Chip color={RISK_STYLE[r.risk_level] || RISK_STYLE[0]}>L{r.risk_level}</Chip></td>
                <td className="py-2 px-3"><Chip color={MODE_STYLE[r.mode] + " border"}>{r.mode}</Chip></td>
                <td className="py-2 px-3 text-slate-600 text-xs">v{r.version}</td>
                <td className="py-2 px-3 text-right space-x-1">
                  <button onClick={() => simulate(r.id)} className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50" data-testid={`auto-sim-${r.id}`}>Simulate</button>
                  <button onClick={() => transition(r.id, "shadow")}     className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">→ Shadow</button>
                  <button onClick={() => transition(r.id, "pilot")}      className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">→ Pilot</button>
                  <button onClick={() => transition(r.id, "production")} className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50" data-testid={`auto-prod-${r.id}`}>→ Prod</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function RuleForm({ onCancel, onSaved }) {
  const [f, setF] = useState({
    name: "", description: "", when_event: "employee.offboarding_started",
    agent_code: "recovery", risk_level: 2, mode: "draft",
    if_conditions: '[{"field":"assigned_assets","op":"gt","value":0}]',
    then_actions:  '[{"kind":"create_task","params":{"title":"Prepare recovery draft"}},{"kind":"create_notification","params":{"channel":"in_app","template":"recovery_ready"}}]',
  });
  const save = async () => {
    try {
      const body = {
        ...f,
        risk_level: Number(f.risk_level),
        if_conditions: JSON.parse(f.if_conditions || "[]"),
        then_actions:  JSON.parse(f.then_actions  || "[]"),
      };
      await api.post("/automation/rules", body);
      toast.success("Automation saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail || e.message)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="auto-rule-form">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Name</div><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="auto-rule-name" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">When (event)</div><input className="input" value={f.when_event} onChange={(e) => setF({ ...f, when_event: e.target.value })} placeholder="employee.offboarding_started" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Agent</div><select className="input" value={f.agent_code} onChange={(e) => setF({ ...f, agent_code: e.target.value })}>{["operations","deployment","recovery","logistics","warehouse","service","customer_success","finance","data_quality","integration","governance"].map((a) => <option key={a}>{a}</option>)}</select></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Risk level (0-4)</div><input type="number" min="0" max="4" className="input" value={f.risk_level} onChange={(e) => setF({ ...f, risk_level: e.target.value })} /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Description</div><input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">IF conditions (JSON array)</div><textarea className="input min-h-[60px] font-mono text-xs" value={f.if_conditions} onChange={(e) => setF({ ...f, if_conditions: e.target.value })} /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">THEN actions (JSON array)</div><textarea className="input min-h-[60px] font-mono text-xs" value={f.then_actions} onChange={(e) => setF({ ...f, then_actions: e.target.value })} /></label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white">Cancel</button>
        <button onClick={save} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="auto-rule-save">Save (draft)</button>
      </div>
    </div>
  );
}

/* ---- Agents ---- */
function Agents() {
  const [d, setD] = useState(null);
  const load = () => api.get("/automation/agents").then(({ data }) => setD(data));
  useEffect(() => { load(); }, []);
  const toggle = async (code, paused) => {
    try { await api.post(`/automation/agents/${code}/toggle?paused=${paused}`); toast.success(`${code} ${paused ? "paused" : "resumed"}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!d) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="auto-agents">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
          <tr><th className="text-left py-2 px-3">Agent</th><th className="text-left py-2 px-3">Reads</th><th className="text-left py-2 px-3">Writes</th><th className="text-left py-2 px-3">Denied</th><th className="text-left py-2 px-3">Ceiling</th><th className="text-right py-2 px-3"></th></tr>
        </thead>
        <tbody>
          {(d.items || []).map((a) => (
            <tr key={a.code} className="border-t border-slate-100" data-testid={`auto-agent-${a.code}`}>
              <td className="py-2 px-3 text-slate-900">{a.name}</td>
              <td className="py-2 px-3 text-slate-600 text-xs">{(a.reads || []).join(", ")}</td>
              <td className="py-2 px-3 text-slate-600 text-xs">{(a.writes || []).join(", ")}</td>
              <td className="py-2 px-3 text-slate-600 text-xs">{(a.denied || []).join(", ")}</td>
              <td className="py-2 px-3"><Chip color={RISK_STYLE[a.risk_ceiling]}>L{a.risk_ceiling}</Chip></td>
              <td className="py-2 px-3 text-right">
                <button onClick={() => toggle(a.code, !a.paused)}
                  className={"text-xs px-2 py-0.5 rounded border inline-flex items-center gap-1 " + (a.paused ? "border-emerald-200 text-emerald-800 bg-emerald-50" : "border-rose-200 text-rose-800 bg-rose-50")}
                  data-testid={`auto-agent-toggle-${a.code}`}>
                  {a.paused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />} {a.paused ? "Resume" : "Pause"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---- Runs ---- */
function Runs() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  useEffect(() => { api.get("/automation/runs", { params: status ? { status } : {} }).then(({ data }) => setItems(data.items || [])); }, [status]);
  return (
    <div className="space-y-2" data-testid="auto-runs">
      <div className="flex gap-1">
        {["", "completed", "awaiting_approval", "shadowed", "simulated", "completed_after_approval"].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)} className={"px-3 py-1.5 rounded-md text-xs border " + (status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700")}>{s || "all"}</button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">When</th><th className="text-left py-2 px-3">Event</th><th className="text-left py-2 px-3">Rule</th><th className="text-left py-2 px-3">Mode</th><th className="text-left py-2 px-3">Status</th><th className="text-left py-2 px-3">Actions</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={6} className="py-6 text-center text-slate-500">No runs yet.</td></tr>
              : items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`auto-run-${r.id}`}>
                  <td className="py-2 px-3 text-slate-700 text-xs">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="py-2 px-3 font-mono text-xs text-slate-700">{r.event}</td>
                  <td className="py-2 px-3 text-xs">{r.rule_id?.slice(0, 8)}</td>
                  <td className="py-2 px-3"><Chip color={MODE_STYLE[r.mode] + " border"}>{r.mode}</Chip></td>
                  <td className="py-2 px-3 text-xs">{r.status}</td>
                  <td className="py-2 px-3 text-xs">{(r.actions_taken || []).map((a) => a.kind).join(", ") || (r.actions_prepared || []).map((a) => a.kind + " (prepared)").join(", ") || "—"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---- Approvals ---- */
function Approvals() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = useCallback(() => api.get("/automation/approvals", { params: { status } }).then(({ data }) => setItems(data.items || [])), [status]);
  useEffect(() => { load(); }, [load]);
  const decide = async (id, decision) => {
    try {
      const reason = decision === "reject" ? window.prompt("Reason for rejection?") || "" : null;
      const { data } = await api.post(`/automation/approvals/${id}/decision`, { decision, reason });
      toast.success(`${decision} · ${(data.executed || []).length} actions executed`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2" data-testid="auto-approvals">
      <div className="flex gap-1">
        {["pending", "approved", "rejected", "modified", "needs_info"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={"px-3 py-1.5 rounded-md text-xs border " + (status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700")}>{s}</button>
        ))}
      </div>
      <ul className="space-y-2">
        {items.length === 0 ? <li className="p-6 bg-white border border-slate-200 rounded-md text-center text-slate-500 text-sm">No {status} approvals.</li>
          : items.map((a) => (
            <li key={a.id} className="bg-white border border-slate-200 rounded-md p-3" data-testid={`auto-appr-${a.id}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">{a.explain?.policy || "Automation proposal"} · <Chip color={RISK_STYLE[a.risk_level] || RISK_STYLE[0]}>L{a.risk_level}</Chip> · <span className="text-slate-500 text-xs">{a.category}</span></div>
                  <div className="text-slate-700 text-sm mt-1">{a.explain?.why}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Proposed: {(a.proposed_actions || []).map((x) => x.kind).join(", ")}</div>
                </div>
                {status === "pending" && (
                  <div className="flex flex-col gap-1">
                    <button onClick={() => decide(a.id, "approve")} className="h-8 px-3 text-xs rounded-md bg-emerald-600 text-white" data-testid={`auto-appr-approve-${a.id}`}>Approve</button>
                    <button onClick={() => decide(a.id, "reject")}  className="h-8 px-3 text-xs rounded-md border border-slate-200 bg-white" data-testid={`auto-appr-reject-${a.id}`}>Reject</button>
                    <button onClick={() => decide(a.id, "needs_info")} className="h-8 px-3 text-xs rounded-md border border-slate-200 bg-white">Info</button>
                  </div>
                )}
              </div>
            </li>
          ))}
      </ul>
    </div>
  );
}

/* ---- Exceptions ---- */
function Exceptions() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/automation/exceptions").then(({ data }) => setItems(data.items || [])); }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="auto-exceptions">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50"><tr><th className="text-left py-2 px-3">When</th><th className="text-left py-2 px-3">Reason</th><th className="text-left py-2 px-3">Severity</th></tr></thead>
        <tbody>
          {items.length === 0 ? <tr><td colSpan={3} className="py-6 text-center text-slate-500">No open exceptions.</td></tr>
            : items.map((e) => (
              <tr key={e.id} className="border-t border-slate-100">
                <td className="py-2 px-3 text-slate-700 text-xs">{e.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="py-2 px-3 text-slate-800">{e.reason}</td>
                <td className="py-2 px-3 text-slate-700 text-xs">{e.severity}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---- Impact ---- */
function Impact() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/automation/impact", { params: { days: 30 } }).then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="text-sm text-slate-500">Loading…</div>;
  const c = d.counts || {};
  return (
    <div className="space-y-3" data-testid="auto-impact">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Automation tasks (30d)" value={c.automation_tasks_created} tone="ok" />
        <Tile label="Notifications"          value={c.notifications_sent} />
        <Tile label="Exceptions raised"      value={c.exceptions_raised} tone="warn" />
        <Tile label="Runs completed"         value={c.runs_completed} tone="ok" />
        <Tile label="Runs shadowed"          value={c.runs_shadowed} />
        <Tile label="Awaiting approval"      value={c.runs_awaiting_approval} tone="warn" />
        <Tile label="Approvals · approved"   value={c.approvals_approved} tone="ok" />
        <Tile label="Failures"               value={c.failures} tone="critical" />
      </div>
      <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 text-xs" data-testid="auto-impact-est">
        <b>ESTIMATED</b> · {d.estimated_impact?.hours_saved_estimated} hours saved · {d.estimated_impact?.methodology}
      </div>
    </div>
  );
}

/* ---- Admin (Kill Switch + Tenant Policy) ---- */
function AdminTab() {
  const [k, setK] = useState(null);
  const load = () => api.get("/automation/kill-switch").then(({ data }) => setK(data));
  useEffect(() => { load(); }, []);
  const setGlobal = async (paused) => {
    try { await api.post("/automation/kill-switch", { paused_global: paused }); toast.success(paused ? "GLOBAL PAUSED" : "Resumed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!k) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="auto-admin">
      <div className={"rounded-md p-4 border " + (k.paused_global ? "bg-rose-50 border-rose-200" : "bg-white border-slate-200")}>
        <div className="flex items-center gap-3">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Kill switch</div>
            <div className="text-lg font-heading font-semibold text-slate-900 mt-1">Global automation is {k.paused_global ? "PAUSED" : "active"}</div>
            <div className="text-xs text-slate-600 mt-1">
              Paused agents: {(k.paused_agents || []).length} · Paused tenants: {(k.paused_tenants || []).length} · Paused rules: {(k.paused_rules || []).length}
            </div>
          </div>
          <div className="ml-auto">
            {k.paused_global ? (
              <button onClick={() => setGlobal(false)} className="h-10 px-4 rounded-md bg-emerald-600 text-white text-sm inline-flex items-center gap-1" data-testid="auto-kill-resume"><Play className="h-4 w-4" /> Resume all</button>
            ) : (
              <button onClick={() => setGlobal(true)}  className="h-10 px-4 rounded-md bg-rose-600 text-white text-sm inline-flex items-center gap-1" data-testid="auto-kill-pause"><Pause className="h-4 w-4" /> Pause all</button>
            )}
          </div>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="text-sm font-medium text-slate-900">Publish a test event</div>
        <div className="text-xs text-slate-500 mb-2">Useful to see what active rules would do. Level 3+ actions land in the Approvals inbox.</div>
        <TestEventForm onSent={() => toast.success("Event published")} />
      </div>
    </div>
  );
}
function TestEventForm({ onSent }) {
  const [f, setF] = useState({ event: "employee.offboarding_started", payload: '{"assigned_assets": 3, "employee_id": "E-DEMO"}', master_company_id: "" });
  const send = async () => {
    try {
      const body = { event: f.event, payload: JSON.parse(f.payload || "{}"), master_company_id: f.master_company_id || undefined };
      await api.post("/automation/events", body);
      onSent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail || e.message)); }
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Event</div><input className="input" value={f.event} onChange={(e) => setF({ ...f, event: e.target.value })} data-testid="auto-event-name" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">master_company_id</div><input className="input" value={f.master_company_id} onChange={(e) => setF({ ...f, master_company_id: e.target.value })} /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Payload (JSON)</div><textarea className="input min-h-[60px] font-mono text-xs" value={f.payload} onChange={(e) => setF({ ...f, payload: e.target.value })} data-testid="auto-event-payload" /></label>
      </div>
      <div className="flex justify-end"><button onClick={send} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="auto-event-send">Publish event</button></div>
    </div>
  );
}
